import { FC, useRef, useState, useMemo } from "react";
import { Icon, Button, Confirm } from "semantic-ui-react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from "../../../../hooks";
import { topMenuItems, menuItemsWithSections } from "../adminMenuConfig.ts";
import "./AdminMenu.scss";

type ButtonProps = {
    onClick: (item: string) => void;
    name: string;
    icon: string;
    to?: string;
    isActive: boolean;
    hasSubNav?: boolean;
};

const NavButton: FC<ButtonProps> = ({
    onClick,
    name,
    icon,
    to,
    isActive,
    hasSubNav,
}) => (
    <Button
        size="big"
        onClick={() => onClick(name, to)}
        className={`${isActive ? "active" : ""} ${hasSubNav ? "has-sub-nav" : ""}`}
    >
        <Icon name={icon} />
        <p>{name}</p>
        {hasSubNav && <Icon name="chevron down" />}
    </Button>
);

type Item = {
    name: string;
    icon: string;
    items: [];
};

type SubMenuProps = {
    item: Item;
    activeItem: string;
    activeSubItem: string;
    handleClick: (args0: string) => void;
};

const SubMenu: FC<SubMenuProps> = ({ item, activeItem, activeSubItem, handleClick }) => {
    const navRef = useRef<HTMLDivElement>(null);

    const isSubNavOpen = (item: string, items: []) =>
        items.some((i) => i.name === activeSubItem) || (item === activeItem && activeSubItem === null);

    const { pathname } = useLocation();

    const isCurrentPath = (path) => {
        if (pathname === '/admin') return false;
        if (path === pathname) return true;
        if (path.includes(pathname)) return true;
        if (typeof path != 'string') {
            if (path.some(str => pathname.includes(str))) return true;
        }
        return false;
    };

    return (
        <div
            className={`sub-nav ${isSubNavOpen(item.name, item.items) ? "open" : ""}`}
            style={{
                height: !isSubNavOpen(item.name, item.items)
                    ? 0
                    : navRef.current?.clientHeight,
            }}
        >
            <div ref={navRef} className="sub-nav-inner">
                {item?.items.map((subItem, index) => (
                    <NavButton
                        key={index}
                        icon={subItem.icon}
                        onClick={() => handleClick(subItem.name, subItem.to)}
                        name={subItem.name}
                        to={subItem.to}
                        isActive={isCurrentPath(subItem.to)}
                    />
                ))}
            </div>
        </div>
    );
};

export const AdminMenu = (props) => {
    const { darkLightTheme, sidebarOpen, onSidebarOpen } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = useLocation();
    const { canAccess, isSuperAdmin, loading: permissionsLoading } = usePermissions();
    const [activeItem, setActiveItem] = useState<string | null>(null);
    const [activeSubItem, setActiveSubItem] = useState<string | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [nextItem, setNextItem] = useState<{ name: string, to: string } | null>(null);

    /**
     * Filter top menu items based on user permissions.
     * Items without a module are always visible.
     */
    const filteredTopMenuItems = useMemo(() => {
        if (permissionsLoading) return [];
        return topMenuItems.filter(item => !item.module || canAccess(item.module));
    }, [canAccess, permissionsLoading]);

    /**
     * Filter grouped menu items based on user permissions.
     * Groups with no visible items are hidden.
     */
    const filteredMenuItems = useMemo(() => {
        if (permissionsLoading) return [];
        return menuItemsWithSections
            .map(group => ({
                ...group,
                items: group.items.filter(item => {
                    if (group.name === "Mantenimiento" && !isSuperAdmin) {
                        return false;
                    }
                    return !item.module || canAccess(item.module);
                })
            }))
            .filter(group => group.items.length > 0);
    }, [canAccess, permissionsLoading, isSuperAdmin]);

    const handleClick = (itemName: string, to: string) => {
        const menuItem = menuItemsWithSections.find(item => item.name === itemName);

        // Si es una categoría padre (tiene items pero no tiene 'to'), solo desplegar/contraer
        if (menuItem && menuItem.items?.length > 0 && !to) {
            if (activeItem === itemName) {
                setActiveItem(null);
                setActiveSubItem(null);
            } else {
                setActiveItem(itemName);
                setActiveSubItem(null);
            }
            return;
        }

        // Detectar si estamos en una ruta que coincide con /admin/media/:id/edit
        const editMediaRegex = /^\/admin\/media\/\d+\/edit$/;
        if (editMediaRegex.test(location.pathname) && to) {
            setNextItem({ name: itemName, to });
            setShowConfirm(true);
            return; // Salir de la función para evitar cambios de estado
        }

        if (!menuItem) {
            // Si el item no es un menú, simplemente navegar
            navigate(to);
            return;
        }

        navigateTo(itemName, to);
    };

    const navigateTo = (itemName: string, to: string) => {
        const menuItem = menuItemsWithSections.find(item => item.name === itemName);
        if (menuItem && menuItem.items?.length > 0) {
            if (activeItem === itemName) {
                // Si el item está activo, contraer el dropdown
                setActiveItem(null);
                setActiveSubItem(null);
            } else {
                // Si se hace clic en un item diferente, actualizar el item activo y cerrar subitems
                setActiveItem(itemName);
                setActiveSubItem(null);
            }
        } else {
            // Si se hace clic en un item sin subitems, actualizar el item activo y cerrar subitems
            setActiveItem(itemName);
            setActiveSubItem(null);
        }
        navigate(to);
    };

    const handleConfirm = () => {
        setShowConfirm(false);
        if (nextItem) {
            navigateTo(nextItem.name, nextItem.to);
            setNextItem(null);
        }
    };

    const handleCancel = () => {
        setShowConfirm(false);
        setNextItem(null);
    };

    const isCurrentPath = (path) => {
        if (pathname === '/admin') return false;
        if (path === pathname) return true;
        if (Array.isArray(path)) {
            return path.some(p =>
                typeof p === 'string' ? pathname.includes(p) : p.test(pathname)
            );
        } else {
            return typeof path === 'string' ? pathname.includes(path) : path.test(pathname);
        }
    };

    const handleSubItemClick = (subItemName: string, to: string) => {
        handleClick(subItemName, to);
        // setActiveSubItem(subItemName);
    };

    const getLogo = darkLightTheme
        ? "url('/img/logo/logo_white.png')"
        : "url('/img/logo/logo.png')";

    return (
        <aside className={`admin-menu ${sidebarOpen ? "open" : ""}`}>
            <header className="sidebar-admin-header">
                <Button
                    size="big"
                    icon={sidebarOpen ? "close" : "bars"}
                    className="sidebar-admin-header__button"
                    onClick={() => onSidebarOpen(!sidebarOpen)}
                >
                </Button>
                <div
                    className={`sidebar-admin-header__logo`} style={{
                        backgroundImage: getLogo,
                        backgroundSize: "contain",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        transition: "background-image 0.5s",
                    }}
                />
            </header>
            <nav className="sidebar-admin-menu">
                {/* Render filtered top-level menu items */}
                {filteredTopMenuItems.map((item, index) => (
                    <NavButton
                        key={`top-${index}`}
                        onClick={handleClick}
                        name={item.name}
                        icon={item.icon}
                        to={item.to}
                        isActive={isCurrentPath(item.pathMatch || item.to)}
                    />
                ))}
                {/* Render filtered grouped menu items */}
                {filteredMenuItems.map((item, index) => (
                    <div key={index}>
                        {!item.items && (
                            <NavButton
                                onClick={handleClick}
                                name={item.name}
                                icon={item.icon}
                                isActive={activeItem === item.name}
                                hasSubNav={!!item.items}
                            />
                        )}
                        {item.items && (
                            <>
                                <NavButton
                                    onClick={handleClick}
                                    name={item.name}
                                    icon={item.icon}
                                    isActive={activeItem === item.name}
                                    hasSubNav={!!item.items}
                                />
                                <SubMenu
                                    activeItem={activeItem}
                                    activeSubItem={activeSubItem}
                                    handleClick={handleSubItemClick}
                                    item={item}
                                />
                            </>
                        )}
                    </div>
                ))}
            </nav>
            <Confirm
                confirmButton="Aceptar"
                cancelButton="Cancelar"
                open={showConfirm}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                content={`Puedes tener cambios pendientes. ¿Estás seguro de que quieres salir sin guardar?`}
                size="mini"
            />
        </aside>
    );
};